import React, { useCallback, useEffect } from 'react';

import { ConfigProvider, Empty, List, Spin } from 'antd';

import { DataFetcher, InferItem, ResponseAdapter } from './types';
import { useInfiniteScroll } from './useInfiniteScroll';

import styles from './DynamicList.module.scss';

export type DynamicListProps<RA extends ResponseAdapter<any, any>> = {
    responseAdapter: RA;
    loadData: DataFetcher<InferItem<RA>, any>;
    renderItem: (item: InferItem<RA>) => React.ReactNode;

    pageSize?: number;
    threshold?: number;
    className?: string;
    emptyText?: React.ReactNode;
    resetDeps?: any[];
    reloadTrigger?: any;
    loader?: React.ReactNode;
    listProps?: Omit<React.ComponentProps<typeof List>, 'dataSource' | 'renderItem' | 'rowKey'>;
    getItemKey?: (item: InferItem<RA>) => React.Key;
    errorRenderer?: (e: Error, retry: () => void) => React.ReactNode;
    onError?: (e: Error) => void;
};

const defaultKey = (item: unknown): React.Key =>
    item && typeof item === 'object' && 'id' in item ? (item as any).id : JSON.stringify(item);

export function DynamicList<RA extends ResponseAdapter<any, any>>({
    responseAdapter,
    loadData,
    renderItem,
    getItemKey,
    pageSize = 10,
    threshold = 0.1,
    className = '',
    emptyText = 'Нет данных',
    resetDeps = [],
    reloadTrigger,
    errorRenderer,
    loader = <Spin />,
    listProps,
    onError,
}: DynamicListProps<RA>) {
    // Item-тип выводится автоматически
    type Item = InferItem<RA>;

    const itemKeyFn = useCallback(
        (item: Item) => (getItemKey ? getItemKey(item) : defaultKey(item)),
        [getItemKey]
    );

    const { items, hasMore, loading, error, loadMore, reset, loaderRef } = useInfiniteScroll<
        Item,
        any
    >(loadData, {
        pageSize,
        threshold,
        resetDeps,
        responseAdapter,
    });

    // Обработчик ошибкок
    useEffect(() => {
        if (error && onError) onError(error);
    }, [error, onError]);

    // Ручной сброс
    useEffect(() => {
        if (reloadTrigger !== undefined) reset();
    }, [reloadTrigger, reset]);

    // Хелпер для рендера элемента списка
    const retry = () => reset();
    const renderError = () =>
        errorRenderer ? (
            errorRenderer(error!, retry)
        ) : (
            <div className={styles.errorContainer}>
                <div className={styles.errorMessage}>{error?.message ?? 'Ошибка'}</div>
                <button onClick={retry} className={styles.retryButton}>
                    Повторить
                </button>
            </div>
        );

    if (!items.length && loading && !error) {
        return <div className={styles.loaderContainer}>{loader}</div>;
    }

    if (error && !items.length) return renderError();

    return (
        <div className={`${styles.dynamicListContainer} ${className}`}>
            {error && renderError()}

            <ConfigProvider renderEmpty={() => <Empty description={emptyText} />}>
                <List
                    // bordered
                    dataSource={items}
                    renderItem={renderItem}
                    // renderItem={((item) => (
                    //     <List.Item key={itemKeyFn(item)}>
                    //         {renderItem(item)}
                    //     </List.Item>
                    // )) as any}
                    rowKey={itemKeyFn}
                    // loading={loading}
                    {...listProps}
                />

                {hasMore && (
                    <div ref={loaderRef} className={styles.autoLoadSentinel}>
                        {loading && <Spin size="small" />}
                    </div>
                )}

                {hasMore && (
                    <div className={styles.loadMore}>
                        <button
                            onClick={loadMore}
                            disabled={loading}
                            className={styles.loadMoreButton}
                        >
                            {loading ? 'Загрузка…' : 'Загрузить ещё'}
                        </button>
                    </div>
                )}
            </ConfigProvider>
        </div>
    );
}
